import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePagination } from '@/hooks/usePagination';
import { Briefcase, Calendar, User, File, Plus, Box, Edit, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/Button';
import { DetailHeader } from '@/components/DetailHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { ModelSelect } from '@/components/ModelSelect';
import { SlideOver } from '@/components/SlideOver';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import { Spinner } from '@/components/Spinner';
import { Alert } from '@/components/Alert';
import { Pagination } from '@/components/Pagination';
import { useToast } from '@/components/ToastProvider';
import { useUIStore } from '@/stores/ui';
import {
  useJob,
  useTasks,
  useJobNotes,
  useCreateJobNote,
  useDeleteJobNote,
  useAddTasks,
  useUpdateTask,
  useCancelTask,
  useJobHistory,
  useUpdateJob,
} from '@/features/jobs/hooks';
import {
  TasksTable,
  TasksSummary,
  TaskFilters,
  NotesList,
  AddNoteForm,
  AddTasksForm,
  UpdateTaskStatusForm,
  UpdateJobStatusForm,
  ChangeHistory,
} from '@/features/jobs/components';
import { formatDistanceToNow } from 'date-fns';
import { formatDate } from '@/utils/formatting';
import type { UpdateJobTaskInput, UpdateJobInput, CreateJobNoteInput, JobTaskStatus } from '@maker-toolchest/shared';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { openAddModelSidebar } = useUIStore();

  const [activeTab, setActiveTab] = useState('overview');
  const [isAddTasksFormOpen, setIsAddTasksFormOpen] = useState(false);
  const [addTasksError, setAddTasksError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isUpdateTaskFormOpen, setIsUpdateTaskFormOpen] = useState(false);
  const [updateTaskError, setUpdateTaskError] = useState<string | null>(null);
  const [isUpdateJobStatusFormOpen, setIsUpdateJobStatusFormOpen] = useState(false);
  const [updateJobStatusError, setUpdateJobStatusError] = useState<string | null>(null);
  const [isEditModelOpen, setIsEditModelOpen] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [updateModelError, setUpdateModelError] = useState<string | null>(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState<JobTaskStatus | ''>('');

  // Pagination
  const tasksPagination = usePagination({ defaultLimit: 25 });
  const notesPagination = usePagination({ defaultLimit: 25 });
  const historyPagination = usePagination({ defaultLimit: 25 });

  const { data: job, isLoading, error } = useJob(id);

  const { data: tasksData } = useTasks({
    jobId: id || '',
    page: tasksPagination.page,
    limit: tasksPagination.limit,
    ...(taskStatusFilter && { status: taskStatusFilter }),
    sortBy: 'taskNumber',
    sortOrder: 'asc',
  });

  const { data: notesData } = useJobNotes(id || '', {
    page: notesPagination.page,
    limit: notesPagination.limit,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: historyData, isLoading: historyLoading } = useJobHistory(id || '', {
    page: historyPagination.page,
    limit: historyPagination.limit,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const tasks = tasksData?.data || [];
  const tasksPageMeta = tasksData?.meta?.pagination;
  const notes = notesData?.data || [];
  const notesPageMeta = notesData?.meta?.pagination;
  const history = historyData?.data || [];
  const historyPageMeta = historyData?.meta?.pagination;

  const addTasks = useAddTasks(id || '');
  const createNote = useCreateJobNote(id || '');
  const deleteNote = useDeleteJobNote(id || '');
  const updateJob = useUpdateJob(id || '');

  // Find selected task from current page's tasks or job's tasks (for Overview tab)
  const selectedTask = selectedTaskId && (tasks.find(t => t.id === selectedTaskId) || job?.tasks.find(t => t.id === selectedTaskId)) || null;
  const updateTask = useUpdateTask(id || '', selectedTaskId || '');
  const cancelTask = useCancelTask(id || '', selectedTaskId || '');

  // Sync selectedModelId with job.modelId when job loads
  useEffect(() => {
    if (job) {
      setSelectedModelId(job.modelId || '');
    }
  }, [job]);

  const handleBack = () => {
    navigate('/projects/jobs');
  };

  const handleStatusClick = (status: JobTaskStatus) => {
    setTaskStatusFilter(status);
    setActiveTab('tasks');
  };

  const handleStatusFilterChange = (status: JobTaskStatus | '') => {
    setTaskStatusFilter(status);
    tasksPagination.setPage(1); // Reset to page 1 when filter changes
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Clear filter when leaving tasks tab
    if (value !== 'tasks') {
      setTaskStatusFilter('');
    }
  };

  const handleAddTasks = async (data: { additionalQuantity: number }) => {
    try {
      setAddTasksError(null);
      await addTasks.mutateAsync(data);
      setIsAddTasksFormOpen(false);
      toast.success(`Added ${data.additionalQuantity} task${data.additionalQuantity > 1 ? 's' : ''}`);
    } catch (error: any) {
      setAddTasksError(error.message || 'Failed to add tasks');
    }
  };

  const handleUpdateTask = async (data: UpdateJobTaskInput) => {
    try {
      setUpdateTaskError(null);
      await updateTask.mutateAsync(data);
      setIsUpdateTaskFormOpen(false);
      setSelectedTaskId(null);
      toast.success('Task updated successfully');
    } catch (error: any) {
      setUpdateTaskError(error.message || 'Failed to update task');
    }
  };

  const handleCancelTask = async () => {
    if (!confirm('Are you sure you want to cancel this task?')) {
      return;
    }

    try {
      await cancelTask.mutateAsync(undefined);
      toast.success('Task cancelled');
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel task');
    }
  };

  const handleCreateNote = async (content: string, isPinned: boolean) => {
    const data: CreateJobNoteInput = { content, isPinned };
    try {
      await createNote.mutateAsync(data);
      toast.success('Note added');
    } catch (error: any) {
      toast.error(error.message || 'Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await deleteNote.mutateAsync(noteId);
      toast.success('Note deleted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete note');
    }
  };

  const handleUpdateModel = async () => {
    try {
      setUpdateModelError(null);
      await updateJob.mutateAsync({
        modelId: selectedModelId || null,
      });
      setIsEditModelOpen(false);
      toast.success('Model association updated');
    } catch (error: any) {
      setUpdateModelError(error.message || 'Failed to update model association');
    }
  };

  const handleUpdateJobStatus = async (data: UpdateJobInput) => {
    try {
      setUpdateJobStatusError(null);
      await updateJob.mutateAsync(data);
      setIsUpdateJobStatusFormOpen(false);
      toast.success('Job status updated successfully');
    } catch (error: any) {
      setUpdateJobStatusError(error.message || 'Failed to update job status');
    }
  };

  const handleCreateAndAssociateModel = () => {
    // Close the edit model modal
    setIsEditModelOpen(false);

    // Open the add model sidebar with a callback to associate the new model
    openAddModelSidebar({
      onSuccess: async (modelId: string) => {
        try {
          // Associate the new model with the job
          await updateJob.mutateAsync({ modelId });
          toast.success('Model created and associated with job');
        } catch (error: any) {
          toast.error(error.message || 'Failed to associate model with job');
        }
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-border bg-background-subtle px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
          >
            Back to Jobs
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
            <p className="text-error">
              {error ? 'Failed to load job details' : 'Job not found'}
            </p>
            <Button variant="secondary" onClick={handleBack} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <DetailHeader
        onBack={handleBack}
        backLabel="Back to Jobs"
        icon={Briefcase}
        iconSize={12}
        title={job.name}
        description={job.description}
        metadata={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsUpdateJobStatusFormOpen(true)}
              className="transition-opacity hover:opacity-80"
              type="button"
            >
              <StatusBadge status={job.status} size="sm" />
            </button>
            <span className="text-xs text-foreground-muted flex items-center gap-1">
              <File className="h-3 w-3" />
              {job.tasks.length} {job.tasks.length === 1 ? 'task' : 'tasks'}
            </span>
            <span className="text-xs text-foreground-muted flex items-center gap-1">
              <User className="h-3 w-3" />
              {job.creator.name}
            </span>
            <span className="text-xs text-foreground-muted flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
            </span>
          </div>
        }
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="border-b border-border px-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks">
              Tasks {tasksPageMeta ? `(${tasksPageMeta.total})` : job.tasks.length ? `(${job.tasks.length})` : ''}
            </TabsTrigger>
            <TabsTrigger value="notes">
              Notes {notesPageMeta ? `(${notesPageMeta.total})` : ''}
            </TabsTrigger>
            <TabsTrigger value="history">
              History {historyPageMeta ? `(${historyPageMeta.total})` : ''}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Overview Tab */}
          <TabsContent value="overview" className="p-6">
            <div className="space-y-6">
              {/* Job Details */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground flex items-center justify-between">
                  <span>Job Details</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Edit className="h-4 w-4" />}
                    onClick={() => setIsUpdateJobStatusFormOpen(true)}
                  >
                    Change Status
                  </Button>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setIsUpdateJobStatusFormOpen(true)}
                    className="rounded-lg border border-border bg-background-subtle p-4 text-left transition-colors hover:bg-background cursor-pointer"
                    type="button"
                  >
                    <div className="text-xs text-foreground-muted">Status</div>
                    <div className="mt-1">
                      <StatusBadge status={job.status} size="sm" />
                    </div>
                  </button>
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <div className="text-xs text-foreground-muted flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Created
                    </div>
                    <div className="mt-1 text-sm font-medium text-foreground">
                      {formatDate(job.createdAt)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Associated Model */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground flex items-center justify-between">
                  <span>Associated Model</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Edit className="h-4 w-4" />}
                    onClick={() => setIsEditModelOpen(true)}
                  >
                    {job.model ? 'Change Model' : 'Associate Model'}
                  </Button>
                </h3>
                {job.model ? (
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <div className="flex items-center gap-4">
                      {job.model.thumbnailUrl && (
                        <img
                          src={job.model.thumbnailUrl}
                          alt={job.model.name}
                          className="h-16 w-16 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground text-sm truncate">
                          {job.model.name}
                        </div>
                        {job.model.description && (
                          <div className="mt-1 text-xs text-foreground-muted line-clamp-2">
                            {job.model.description}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<LinkIcon className="h-4 w-4" />}
                        onClick={() =>
                          navigate(`/models/${job.model!.id}`, {
                            state: {
                              from: {
                                label: `Back to ${job.name}`,
                                path: `/projects/jobs/${job.id}`,
                              },
                            },
                          })
                        }
                      >
                        View Model
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-border border-dashed bg-background-subtle p-8 text-center">
                    <Box className="h-12 w-12 mx-auto text-foreground-muted opacity-50" />
                    <p className="mt-2 text-sm text-foreground-muted">
                      No model associated with this job
                    </p>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-4"
                      onClick={() => setIsEditModelOpen(true)}
                    >
                      Associate Model
                    </Button>
                  </div>
                )}
              </div>

              {/* Tasks Summary */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground">Tasks Overview</h3>
                <TasksSummary tasks={job.tasks} onStatusClick={handleStatusClick} />
              </div>

              {/* Objects section (if any) */}
              {job.objects && job.objects.length > 0 && (
                <div>
                  <h3 className="mb-3 text-lg font-medium text-foreground">
                    Objects ({job.objects.length})
                  </h3>
                  <div className="space-y-2">
                    {job.objects.map((obj) => (
                      <div
                        key={obj.id}
                        className="rounded-lg border border-border bg-background-subtle p-4"
                      >
                        <div className="font-medium text-foreground text-sm">
                          Quantity: {obj.quantity}
                        </div>
                        {obj.overrideColorHex && (
                          <div className="mt-1 text-sm text-foreground-muted">
                            Color: {obj.overrideColorHex}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  All Tasks {tasksPageMeta && `(${tasksPageMeta.total})`}
                </h3>
                <Button
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsAddTasksFormOpen(true)}
                >
                  Add Tasks
                </Button>
              </div>
              <TaskFilters
                status={taskStatusFilter}
                onStatusChange={handleStatusFilterChange}
              />
              <TasksTable
                tasks={tasks}
                onUpdateStatus={(taskId) => {
                  setSelectedTaskId(taskId);
                  setIsUpdateTaskFormOpen(true);
                }}
                onCancel={(taskId) => {
                  setSelectedTaskId(taskId);
                  handleCancelTask();
                }}
                statusFilter={taskStatusFilter}
              />
              {tasksPageMeta && (
                <Pagination
                  page={tasksPageMeta.page}
                  total={tasksPageMeta.total}
                  limit={tasksPageMeta.limit}
                  onPageChange={tasksPagination.setPage}
                />
              )}
            </div>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  Job Notes {notesPageMeta && `(${notesPageMeta.total})`}
                </h3>
              </div>
              <AddNoteForm
                onSubmit={handleCreateNote}
                loading={createNote.isPending}
              />
              {notes && notes.length > 0 && (
                <NotesList
                  notes={notes}
                  onDelete={handleDeleteNote}
                  loading={deleteNote.isPending}
                />
              )}
              {notesPageMeta && (
                <Pagination
                  page={notesPageMeta.page}
                  total={notesPageMeta.total}
                  limit={notesPageMeta.limit}
                  onPageChange={notesPagination.setPage}
                />
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">
                  Change History {historyPageMeta && `(${historyPageMeta.total})`}
                </h3>
              </div>
              <ChangeHistory history={history} loading={historyLoading} />
              {historyPageMeta && (
                <Pagination
                  page={historyPageMeta.page}
                  total={historyPageMeta.total}
                  limit={historyPageMeta.limit}
                  onPageChange={historyPagination.setPage}
                />
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Add Tasks Form */}
      <AddTasksForm
        open={isAddTasksFormOpen}
        onClose={() => {
          setIsAddTasksFormOpen(false);
          setAddTasksError(null);
        }}
        onSubmit={handleAddTasks}
        currentQuantity={job.quantity}
        loading={addTasks.isPending}
        error={addTasksError}
      />

      {/* Update Task Status Form */}
      {selectedTask && (
        <UpdateTaskStatusForm
          open={isUpdateTaskFormOpen}
          onClose={() => {
            setIsUpdateTaskFormOpen(false);
            setSelectedTaskId(null);
            setUpdateTaskError(null);
          }}
          onSubmit={handleUpdateTask}
          currentStatus={selectedTask.status}
          currentProgress={selectedTask.progress}
          loading={updateTask.isPending}
          error={updateTaskError}
        />
      )}

      {/* Update Job Status Form */}
      <UpdateJobStatusForm
        open={isUpdateJobStatusFormOpen}
        onClose={() => {
          setIsUpdateJobStatusFormOpen(false);
          setUpdateJobStatusError(null);
        }}
        onSubmit={handleUpdateJobStatus}
        currentStatus={job.status}
        loading={updateJob.isPending}
        error={updateJobStatusError}
      />

      {/* Edit Model Association */}
      <SlideOver
        open={isEditModelOpen}
        onClose={() => {
          setIsEditModelOpen(false);
          setUpdateModelError(null);
          setSelectedModelId(job?.modelId || '');
        }}
        title="Associate Model"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsEditModelOpen(false);
                setUpdateModelError(null);
                setSelectedModelId(job?.modelId || '');
              }}
              disabled={updateJob.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpdateModel}
              disabled={updateJob.isPending}
              loading={updateJob.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {updateModelError && (
            <Alert variant="error" message={updateModelError} />
          )}
          <div>
            <label htmlFor="model" className="block text-sm font-medium text-foreground mb-1">
              Select Model
            </label>
            <ModelSelect
              value={selectedModelId}
              onChange={setSelectedModelId}
              disabled={updateJob.isPending}
              allowEmpty={true}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-foreground-muted">
                Choose a 3D model to associate with this job, or select "No model selected" to remove the association.
              </p>
              <button
                type="button"
                onClick={handleCreateAndAssociateModel}
                disabled={updateJob.isPending}
                className="text-xs text-accent hover:text-accent-hover font-medium transition-colors whitespace-nowrap ml-2"
              >
                + Create New Model
              </button>
            </div>
          </div>
        </div>
      </SlideOver>
    </div>
  );
}
